import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, type MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useDeleteJD, useMoveJD, type JDDetail, type JDStatusName } from "@/api/jd-queries";
import { useCan } from "@/auth/member";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Menu, type MenuItem } from "@/components/ui/menu";
import { ErrorNote } from "@/components/ui/panel";
import { experience, publishBlock, STATUS_MEANING } from "@/lib/jd-steps";
import { cn, formatDate } from "@/lib/utils";

/**
 * The top of a job description's page — DESIGN §20.8, §20.11. One primary action for the status it is
 * in, the rest under More, and the reason a disabled Publish is disabled written beside it: a title
 * attribute was the only place it said so before, and nobody hovers a disabled button to find out.
 */

type Move = { to: JDStatusName; label: string; hint?: string };

/** The one action each status leads to, and the moves that are rarer. */
const MOVES: Record<JDStatusName, { primary: Move | null; more: Move[] }> = {
  in_review: {
    primary: { to: "active", label: "Publish" },
    more: [{ to: "discarded", label: "Discard this position", hint: "Out of use. Keep it brings it back." }],
  },
  active: {
    primary: null,
    more: [
      {
        to: "archived",
        label: "Archive",
        hint: "Takes it off the start form. Interviews already started are not affected.",
      },
    ],
  },
  archived: {
    primary: { to: "active", label: "Publish again" },
    more: [{ to: "in_review", label: "Back to review" }],
  },
  discarded: { primary: { to: "in_review", label: "Keep it" }, more: [] },
};

const MEANING_TONE: Record<JDStatusName, string> = {
  in_review: "text-bank-attention-ink",
  active: "text-bank-good-ink",
  archived: "text-bank-muted",
  discarded: "text-bank-muted",
};

export function JDHeader({
  jd,
  dirty,
  onLeave,
  onShowText,
}: {
  jd: JDDetail;
  dirty: boolean;
  onLeave: (e: MouseEvent) => void;
  onShowText: () => void;
}) {
  const canManage = useCan("manage_jd");
  const canPurge = useCan("purge_jd");
  const move = useMoveJD(jd.jd.id);
  const remove = useDeleteJD(jd.jd.id);
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const status = jd.jd.status;
  const moves = MOVES[status];
  const primary = canManage ? moves.primary : null;
  const block = primary?.to === "active" ? publishBlock(jd, dirty) : dirty ? "Save your changes first" : null;

  const siblings = [...jd.siblings].sort((a, b) => a.position_index - b.position_index);
  const at = siblings.findIndex((s) => s.id === jd.jd.id);
  const goTo = (id: number | undefined) => {
    if (id === undefined) return;
    if (dirty && !window.confirm("You have unsaved changes on this job description. Leave without saving?"))
      return;
    void navigate(`/jds/${id}`);
  };

  const more: (MenuItem | "divider")[] = [];
  if (canManage) {
    more.push({ label: "View the client's original text", onSelect: onShowText });
    for (const m of moves.more) {
      more.push({
        label: m.label,
        hint: dirty ? "Save or discard your changes first." : m.hint,
        disabled: dirty || move.isPending,
        onSelect: () => move.mutate({ status: m.to }),
      });
    }
  }
  if (canPurge) {
    if (more.length) more.push("divider");
    more.push({
      label: "Delete permanently…",
      hint: status === "active" ? "Archive it first — a live JD can't be deleted." : undefined,
      disabled: status === "active",
      danger: true,
      onSelect: () => setConfirmDelete(true),
    });
  }

  const client = jd.jd.client_name
    ? `${jd.jd.client_name} (${jd.jd.show_client ? "shown to members" : "hidden from members"})`
    : "No client named";

  return (
    <>
      <nav className="text-bank-muted flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/jds" onClick={onLeave} className="text-bank-accent-ink hover:underline">
          Job descriptions
        </Link>
        <span aria-hidden="true">›</span>
        <span className="truncate">{jd.jd.title}</span>
      </nav>

      <header className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-[1_1_420px]">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="text-base font-semibold tracking-tight">{jd.jd.title}</h1>
            <Badge value={status} />
            <span className={cn("text-[12.5px]", MEANING_TONE[status])}>
              {status === "active" && jd.jd.published_at
                ? `Live on ipp-platform since ${formatDate(jd.jd.published_at)}${jd.published_by ? ` · published by ${jd.published_by}` : ""}`
                : STATUS_MEANING[status]}
            </span>
          </div>
          <p className="text-bank-muted mt-1 text-[12.5px]">
            {client} · {experience(jd.jd.experience_min, jd.jd.experience_max)} · from{" "}
            <span className="font-mono text-[11.5px]">{jd.source.filename ?? "pasted text"}</span> · uploaded{" "}
            {formatDate(jd.source.created_at)}
            {jd.source.uploaded_by ? ` by ${jd.source.uploaded_by}` : ""}
          </p>
        </div>

        <div className="flex flex-wrap items-start justify-end gap-2">
          {siblings.length > 1 && (
            <div className="border-bank-border-strong bg-bank-surface inline-flex items-center rounded-md border">
              <Button
                size="sm"
                variant="ghost"
                aria-label="Previous position from the same upload"
                disabled={at <= 0}
                onClick={() => goTo(siblings[at - 1]?.id)}
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
              </Button>
              <Menu
                label="Positions from the same upload"
                size="sm"
                variant="ghost"
                className="border-bank-border rounded-none border-x"
                trigger={
                  <>
                    Position {at + 1} of {siblings.length}
                    <ChevronDown className="size-3.5" aria-hidden="true" />
                  </>
                }
                items={siblings.map((s, i) => ({
                  label: `${i + 1}. ${s.title}`,
                  hint: s.id === jd.jd.id ? `${s.status} · this one` : s.status,
                  disabled: s.id === jd.jd.id,
                  onSelect: () => goTo(s.id),
                }))}
              />
              <Button
                size="sm"
                variant="ghost"
                aria-label="Next position from the same upload"
                disabled={at < 0 || at >= siblings.length - 1}
                onClick={() => goTo(siblings[at + 1]?.id)}
              >
                <ChevronRight className="size-4" aria-hidden="true" />
              </Button>
            </div>
          )}
          {more.length > 0 && (
            <Menu
              label="More actions for this job description"
              items={more}
              trigger={
                <>
                  More
                  <ChevronDown className="size-4" aria-hidden="true" />
                </>
              }
            />
          )}
          {primary && (
            <div className="flex flex-col items-end">
              <Button
                variant="primary"
                disabled={move.isPending || block !== null}
                onClick={() => move.mutate({ status: primary.to })}
              >
                {move.isPending ? "Working…" : primary.label}
              </Button>
              {block && <span className="text-bank-attention-ink mt-0.5 text-[11.5px]">{block}</span>}
            </div>
          )}
        </div>
      </header>
      <ErrorNote error={move.error} />

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete “${jd.jd.title}” permanently?`}
        confirmLabel="Delete permanently"
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate(undefined, { onSuccess: () => void navigate("/jds") })}
      >
        Its skills, question links and history are removed and cannot be brought back
        {jd.siblings.length <= 1
          ? ", and so is the uploaded document, as no other position is read from it"
          : ""}
        . Questions already in the bank stay.
      </ConfirmDialog>
    </>
  );
}
